import React, { useState } from 'react';

interface NavbarProps {
  expand?: string;
  bg?: string;
  variant?: string;
  fixed?: 'top' | 'bottom';
  className?: string;
  children?: React.ReactNode;
  'aria-label'?: string;
}

function NavbarBase({ expand, bg, variant, fixed, className = '', children, ...rest }: NavbarProps) {
  const [expanded, setExpanded] = useState(false);
  const bgClass = bg === 'dark' ? 'bg-neutral text-neutral-content' : bg ? 'bg-base-200' : '';
  const fixedClass = fixed === 'top' ? 'fixed top-0 w-full z-30' : fixed === 'bottom' ? 'fixed bottom-0 w-full z-30' : '';

  return (
    <nav className={`navbar ${bgClass} ${fixedClass} ${className}`.trim()} {...rest}>
      {React.Children.map(children, child => {
        if (!React.isValidElement(child)) return child;
        return React.cloneElement(child as React.ReactElement<any>, { _expanded: expanded, _setExpanded: setExpanded, _expand: expand });
      })}
    </nav>
  );
}

interface SubProps {
  className?: string;
  children?: React.ReactNode;
}

function NavbarText({ className = '', children }: SubProps) {
  return <span className={className}>{children}</span>;
}

interface ToggleProps extends SubProps {
  'aria-controls'?: string;
  _expanded?: boolean;
  _setExpanded?: (v: boolean) => void;
}

function NavbarToggle({ className = '', _expanded, _setExpanded, ...rest }: ToggleProps) {
  return (
    <button
      type="button"
      className={`btn btn-ghost btn-sm lg:hidden ${className}`.trim()}
      onClick={() => _setExpanded?.(!_expanded)}
      aria-expanded={_expanded}
      {...rest}
    >
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="inline-block h-5 w-5 stroke-current">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
      </svg>
    </button>
  );
}

interface CollapseProps extends SubProps {
  id?: string;
  _expanded?: boolean;
  _setExpanded?: (v: boolean) => void;
  _expand?: string;
}

function NavbarCollapse({ id, className = '', children, _expanded, _expand }: CollapseProps) {
  const breakpointClass = _expand === 'lg' ? 'lg:flex' : _expand === 'md' ? 'md:flex' : 'flex';
  const hiddenClass = _expanded ? 'flex' : `hidden ${breakpointClass}`;
  return (
    <div className={`navbar-collapse ${hiddenClass} ${className}`.trim()} id={id}>
      {children}
    </div>
  );
}

const Navbar = Object.assign(NavbarBase, {
  Text: NavbarText,
  Toggle: NavbarToggle,
  Collapse: NavbarCollapse,
});

export default Navbar;
